import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { isEmpty } from 'lodash';

/**
 * User Settings
 */
export interface SettingsBase<ColumnKeys extends string | number | symbol> {
  /**
   * The page size of the grid.
   */
  pageSize: number;
  /**
   * Which columns are visible.
   */
  displayedColumns: ColumnKeys[];
  /**
   * The order of the grid columns.
   */
  columnOrder: ColumnKeys[];
  /**
   * For each column key, how big the column in percent or pixel is.
   */
  columnsWidths: Partial<Record<ColumnKeys, string>>;
}

/**
 * User Settings from api
 */
export interface UserSettingsDto {
  machineHistorySettings: SettingsBase<any>;
  machineTaskSettings: SettingsBase<any>;
  toolingTaskHistorySettings: SettingsBase<any>;
  toolingTaskSettings: SettingsBase<any>;
}

/**
 * Abstract base class for creating grid settings.
 */
export abstract class SettingsApiBase<
  ColumnKeys extends string,
  Settings extends SettingsBase<ColumnKeys>
> {
  constructor(
    private updateSettingsUrl: string,
    private http: HttpClient,
    private settingsDefaults: Settings,
    private settingsKey: string,
    private getSettingsUrl: string,
    private dataField: keyof UserSettingsDto
  ) {}

  /**
   * Persist settings.
   */
  setSettings(settings: Settings) {
    this.http.put(this.updateSettingsUrl, this.formatForUpdate(settings)).subscribe();
  }

  private formatForUpdate(settings: Settings) {
    for (const prop in settings.columnsWidths) {
      settings.columnsWidths[prop] =
        settings.columnsWidths[prop] && typeof settings.columnsWidths[prop] === 'string'
          ? (+settings.columnsWidths[prop].replace('px', '') as any)
          : settings.columnsWidths[prop];
    }

    return settings;
  }

  /**
   * Load settings.
   */
  loadSettings$(): Observable<Settings> {
    return this.http.get<any>(this.getSettingsUrl).pipe(
      map((settings) => settings[this.dataField]),
      map((settings: Settings) =>
        this.checkSettingsOnEmpty<Settings>(settings || ({} as any), this.settingsDefaults)
      ),
      catchError(() => of(this.settingsKey)),
      map((settings) => settings as Settings)
    );
  }

  private checkSettingsOnEmpty<T>(settings: T, defaultSettings: T): T {
    for (const key in defaultSettings) {
      settings[key] = isEmpty(settings[key]) ? defaultSettings[key] : settings[key];
    }
    return settings;
  }
}
